/**
 * Success state after form submission, with the SAT verification result.
 * Focus management for WCAG 2.4.3 compliance.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { cn } from "~/lib/utils";
import { AlertTriangle, CheckCircle2, Info, Loader2, Lock, type LucideIcon } from "lucide-react";
import type { VerificationView } from "~/features/onboarding/verification";

// clases completas (Tailwind solo genera las que ve escritas)
const TONES = {
  success: {
    card: "border-success/50",
    badge: "bg-success/10",
    text: "text-success",
    note: "bg-primary/5 border-primary/20",
    noteIcon: "text-primary",
  },
  destructive: {
    card: "border-destructive/50",
    badge: "bg-destructive/10",
    text: "text-destructive",
    note: "bg-destructive/5 border-destructive/20",
    noteIcon: "text-destructive",
  },
  neutral: {
    card: "",
    badge: "bg-primary/10",
    text: "text-primary",
    note: "bg-primary/5 border-primary/20",
    noteIcon: "text-primary",
  },
} as const;

interface Variant {
  tone: keyof typeof TONES;
  icon: LucideIcon;
  spin?: boolean;
  key: string; // onboarding.verification.<key>.{title,description}
}

const VARIANTS: Record<VerificationView, Variant> = {
  checking: { tone: "neutral", icon: Loader2, spin: true, key: "checking" },
  verified: { tone: "success", icon: CheckCircle2, key: "verified" },
  credentials_rejected: { tone: "destructive", icon: AlertTriangle, key: "credentialsRejected" },
  account_locked: { tone: "destructive", icon: Lock, key: "accountLocked" },
  continues: { tone: "success", icon: CheckCircle2, key: "continues" },
};

interface SuccessStateProps {
  verification: VerificationView;
}

export function SuccessState({ verification }: SuccessStateProps) {
  const { t } = useTranslation();
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const variant = VARIANTS[verification];
  const tone = TONES[variant.tone];
  const Icon = variant.icon;

  // Focus on heading when the result changes, for screen reader users
  React.useEffect(() => {
    headingRef.current?.focus();
  }, [verification]);

  return (
    <div className="w-full py-6 sm:py-12 px-0 sm:px-4">
      <div className="mx-auto max-w-lg">
        <Card className={cn("rounded-none sm:rounded-lg border-x-0 sm:border-x", tone.card)}>
          <CardHeader className="text-center">
            <div
              className={cn("mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full", tone.badge)}
              aria-hidden="true"
            >
              <Icon className={cn("h-8 w-8", tone.text, variant.spin && "animate-spin")} />
            </div>
            <CardTitle
              ref={headingRef}
              tabIndex={-1}
              as="h1"
              className={cn("text-2xl outline-none", tone.text)}
            >
              {t(`onboarding.verification.${variant.key}.title`)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={cn("rounded-lg border p-4", tone.note)}
              role="status"
              aria-live="polite"
              aria-busy={verification === "checking"}
            >
              <div className="flex items-start gap-3">
                <Info className={cn("h-5 w-5 mt-0.5 flex-shrink-0", tone.noteIcon)} aria-hidden="true" />
                <p className="text-sm text-muted-foreground">
                  {t(`onboarding.verification.${variant.key}.description`)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
